import type { CardData, RarityTier } from '@gachakit/core/types/card';

interface CardVisualMeta {
  cardCode?: unknown;
  typeLabel?: unknown;
  hp?: unknown;
  spellName?: unknown;
  spellCost?: unknown;
  spellPower?: unknown;
  flavor?: unknown;
  weakness?: unknown;
  resistance?: unknown;
  retreat?: unknown;
  finish?: unknown;
}

const styles = `
  :host {
    display: block;
    --rarity-color: #9eaa9b;
  }

  .card {
    position: relative;
    min-height: 438px;
    min-width: 0;
    overflow: hidden;
    border-radius: 18px;
    padding: 8px;
    color: #11110c;
    border: 1px solid color-mix(in oklab, var(--rarity-color) 50%, rgba(255,255,255,0.2));
    background:
      linear-gradient(135deg, rgba(255,255,255,0.72), rgba(255,255,255,0.08) 18%, rgba(255,255,255,0.38) 40%, rgba(0,0,0,0.22) 66%, rgba(255,255,255,0.5)),
      conic-gradient(from 150deg, #d8d5c7, var(--rarity-color), #f7efda, #9b9b90, var(--rarity-color), #f7efda);
    box-shadow:
      0 20px 46px rgba(0, 0, 0, 0.42),
      0 0 0 1px rgba(255,255,255,0.18) inset;
    isolation: isolate;
  }

  .card::before,
  .card::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  .card::before {
    background:
      linear-gradient(115deg, transparent 0 30%, rgba(255,255,255,0.55) 43%, transparent 56%),
      repeating-linear-gradient(135deg, rgba(255,255,255,0.12) 0 1px, transparent 1px 9px);
    mix-blend-mode: screen;
    opacity: 0.34;
    transform: translateX(-18%);
  }

  .card::after {
    inset: 8px;
    border-radius: 13px;
    border: 1px solid rgba(255,255,255,0.42);
    box-shadow: inset 0 0 0 1px rgba(0,0,0,0.28);
  }

  .card[data-rarity='common'] { --rarity-color: var(--rarity-common, #9eaa9b); }
  .card[data-rarity='uncommon'] { --rarity-color: var(--rarity-uncommon, #58d68d); }
  .card[data-rarity='rare'] { --rarity-color: var(--rarity-rare, #68c7ff); }
  .card[data-rarity='epic'] { --rarity-color: var(--rarity-epic, #e16f9d); }
  .card[data-rarity='legendary'] { --rarity-color: var(--rarity-legendary, #f5c76b); }

  .card[data-premium='true']::before {
    animation: foil-drift 5.2s ease-in-out infinite;
    opacity: 0.52;
  }

  .card[data-finish='holo'] {
    background:
      linear-gradient(135deg, rgba(255,255,255,0.78), rgba(255,255,255,0.12) 18%, rgba(255,255,255,0.5) 40%, rgba(0,0,0,0.18) 66%, rgba(255,255,255,0.62)),
      conic-gradient(from 90deg, #ff8bd1, #8ef7ff, #f8ef7a, #b68cff, #87ffb0, #ff8bd1);
  }

  .card[data-finish='holo']::before,
  .card[data-finish='reverse-holo']::before {
    opacity: 0.78;
    background:
      linear-gradient(115deg, transparent 0 26%, rgba(255,255,255,0.72) 42%, transparent 58%),
      repeating-linear-gradient(120deg, rgba(255,255,255,0.22) 0 1px, transparent 1px 7px),
      conic-gradient(from 180deg, transparent, rgba(255,0,180,0.22), rgba(0,220,255,0.22), rgba(255,230,0,0.22), transparent);
    animation: foil-drift 3.8s ease-in-out infinite;
  }

  .card[data-finish='reverse-holo'] {
    background:
      radial-gradient(circle at 22% 18%, rgba(255,255,255,0.78), transparent 18%),
      repeating-linear-gradient(45deg, rgba(255,255,255,0.22) 0 2px, rgba(255,255,255,0.02) 2px 7px),
      conic-gradient(from 140deg, #f7efda, #7df4ff, #f679e8, #f5c76b, #94ffbd, #f7efda);
  }

  .card[data-finish='reverse-holo'] .inner {
    background:
      linear-gradient(180deg, rgba(255,255,255,0.82), rgba(255,247,218,0.68) 42%, rgba(237,205,126,0.74)),
      repeating-linear-gradient(135deg, rgba(255,255,255,0.28) 0 1px, transparent 1px 8px),
      #efe3b8;
  }

  .inner {
    position: relative;
    z-index: 1;
    min-height: 100%;
    min-width: 0;
    display: grid;
    grid-template-rows: auto auto auto auto minmax(0, 1fr) auto;
    gap: 8px;
    padding: 9px;
    border-radius: 12px;
    background:
      linear-gradient(180deg, rgba(255,255,255,0.74), rgba(255,247,218,0.58) 42%, rgba(237,205,126,0.64)),
      #efe3b8;
    border: 1px solid rgba(55, 42, 18, 0.28);
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.48);
  }

  .topline {
    min-width: 0;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 7px;
    min-height: 44px;
    padding: 5px;
    border-radius: 10px;
    background:
      linear-gradient(180deg, rgba(255,255,255,0.76), rgba(255,255,255,0.16)),
      color-mix(in oklab, var(--rarity-color) 18%, #f4ead1);
    border: 1px solid rgba(50, 36, 15, 0.18);
  }

  .type-pill,
  .hp,
  .status-pill,
  .serial {
    min-width: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    font-family: var(--font-display, "Sora", sans-serif);
    letter-spacing: 0;
  }

  .type-pill {
    min-height: 28px;
    padding: 0 9px;
    border-radius: 999px;
    color: #271900;
    background: linear-gradient(180deg, #ffffff, #cac5b5);
    border: 1px solid rgba(39, 25, 0, 0.22);
    font-size: 0.66rem;
    font-weight: 900;
    text-transform: uppercase;
  }

  .nameplate {
    min-width: 0;
    display: grid;
    gap: 2px;
  }

  h3 {
    min-width: 0;
    margin: 0;
    color: #0f0d08;
    font: 900 1.22rem/1 var(--font-display, "Sora", sans-serif);
    letter-spacing: 0;
    overflow-wrap: anywhere;
  }

  .subtitle {
    min-width: 0;
    color: rgba(20, 16, 8, 0.66);
    font: 800 0.68rem/1 var(--font-display, "Sora", sans-serif);
  }

  .hp {
    gap: 4px;
    color: #120d04;
    font-weight: 900;
  }

  .hp strong {
    font-size: 1.02rem;
    line-height: 1;
  }

  .hp small {
    font-size: 0.58rem;
    line-height: 1;
  }

  .hp i {
    width: 24px;
    aspect-ratio: 1;
    display: block;
    border-radius: 50%;
    background:
      radial-gradient(circle at 32% 28%, #ffffff, transparent 24%),
      radial-gradient(circle at 50% 64%, color-mix(in oklab, var(--rarity-color) 72%, #ffffff), var(--rarity-color));
    border: 1px solid rgba(40, 24, 0, 0.28);
    box-shadow: inset 0 0 0 2px rgba(255,255,255,0.28);
  }

  .art-frame {
    position: relative;
    min-width: 0;
    overflow: hidden;
    border-radius: 8px;
    aspect-ratio: 0.86;
    border: 5px solid rgba(58, 37, 9, 0.72);
    background:
      linear-gradient(145deg, color-mix(in oklab, var(--rarity-color) 24%, transparent), rgba(255,255,255,0.14)),
      #10130f;
    box-shadow:
      inset 0 0 0 1px rgba(255,255,255,0.34),
      0 10px 20px rgba(40, 22, 0, 0.26);
  }

  .art-frame::before {
    content: attr(data-fallback);
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    color: rgba(255,255,255,0.72);
    font: 900 3.2rem/1 var(--font-display, "Sora", sans-serif);
    background:
      linear-gradient(135deg, color-mix(in oklab, var(--rarity-color) 28%, transparent), rgba(255,255,255,0.04)),
      repeating-linear-gradient(45deg, rgba(255,255,255,0.035) 0 1px, transparent 1px 12px);
  }

  .art-frame::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, transparent 58%, rgba(0,0,0,0.28)),
      linear-gradient(120deg, transparent 0 38%, rgba(255,255,255,0.2) 48%, transparent 60%);
    pointer-events: none;
  }

  img {
    position: relative;
    z-index: 1;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    transform: scale(1.02);
    transition: opacity 320ms ease, transform 520ms ease;
  }

  img[data-loaded='true'] {
    opacity: 1;
    transform: scale(1);
  }

  .spell-row {
    min-width: 0;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    min-height: 58px;
    padding: 8px;
    border-radius: 8px;
    color: #120d04;
    background:
      linear-gradient(180deg, rgba(255,255,255,0.62), rgba(255,255,255,0.16)),
      rgba(255, 246, 210, 0.84);
    border-top: 3px solid rgba(30, 22, 8, 0.62);
    border-bottom: 1px solid rgba(30, 22, 8, 0.24);
  }

  .cost {
    display: flex;
    align-items: center;
    gap: 3px;
  }

  .mana {
    width: 18px;
    aspect-ratio: 1;
    display: block;
    border-radius: 50%;
    background:
      radial-gradient(circle at 32% 26%, #fff9d6, transparent 28%),
      linear-gradient(135deg, color-mix(in oklab, var(--rarity-color) 80%, #ffffff), var(--rarity-color));
    border: 1px solid rgba(43, 27, 0, 0.3);
    box-shadow: inset 0 0 0 2px rgba(255,255,255,0.28);
  }

  .spell-copy {
    min-width: 0;
    display: grid;
    gap: 3px;
  }

  .spell-copy strong {
    color: #080705;
    font: 900 1.04rem/1.02 var(--font-display, "Sora", sans-serif);
  }

  .spell-copy span {
    color: rgba(22, 18, 10, 0.68);
    font: 800 0.73rem/1.18 var(--font-body, "Manrope", sans-serif);
  }

  .power {
    min-width: 34px;
    text-align: right;
    color: #080705;
    font: 900 1.28rem/1 var(--font-display, "Sora", sans-serif);
  }

  .flavor {
    min-width: 0;
    margin: 0;
    padding: 8px 10px;
    border-top: 1px solid rgba(34, 24, 7, 0.4);
    border-bottom: 1px solid rgba(255,255,255,0.45);
    color: rgba(20, 16, 8, 0.68);
    font: italic 0.78rem/1.25 var(--font-body, "Manrope", sans-serif);
  }

  .traits {
    min-width: 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
  }

  .trait {
    min-width: 0;
    display: grid;
    gap: 3px;
    padding: 7px 6px;
    border-radius: 7px;
    background: rgba(255,255,255,0.25);
    border: 1px solid rgba(38, 28, 10, 0.16);
    text-align: center;
  }

  .trait small {
    color: rgba(20, 16, 8, 0.58);
    font: 900 0.54rem/1 var(--font-display, "Sora", sans-serif);
    text-transform: uppercase;
  }

  .trait strong {
    color: #120d04;
    font: 900 0.66rem/1.05 var(--font-display, "Sora", sans-serif);
  }

  .footer {
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    color: rgba(20, 16, 8, 0.72);
  }

  .status-pill,
  .serial {
    min-height: 26px;
    padding: 0 8px;
    border-radius: 999px;
    background: rgba(255,255,255,0.24);
    border: 1px solid rgba(38, 28, 10, 0.16);
    font-size: 0.62rem;
    font-weight: 900;
    text-transform: uppercase;
  }

  .status-pill {
    color: #0c0b08;
  }

  .serial {
    margin-left: auto;
  }

  .link {
    flex: 0 0 auto;
    min-height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 8px;
    border-radius: 999px;
    border: 1px solid rgba(38, 28, 10, 0.16);
    color: #0c0b08;
    background: rgba(255,255,255,0.24);
    text-decoration: none;
    font: 900 0.62rem/1 var(--font-display, "Sora", sans-serif);
    text-transform: uppercase;
  }

  .card.compact {
    height: 100%;
    min-height: 0;
    border-radius: 14px;
  }

  .card.compact .inner {
    gap: 6px;
    padding: 7px;
    grid-template-rows: auto minmax(0, 1fr) auto auto;
  }

  .card.compact .topline {
    min-height: 36px;
    gap: 5px;
    padding: 4px;
  }

  .card.compact .type-pill {
    min-height: 24px;
    padding-inline: 7px;
    font-size: 0.58rem;
  }

  .card.compact h3 {
    font-size: 0.94rem;
    line-height: 1.02;
  }

  .card.compact .subtitle {
    font-size: 0.6rem;
  }

  .card.compact .hp strong {
    font-size: 0.86rem;
  }

  .card.compact .hp small {
    display: none;
  }

  .card.compact .hp i {
    width: 18px;
  }

  .card.compact .art-frame {
    border-width: 4px;
    aspect-ratio: 1.04;
  }

  .card.compact .spell-row {
    min-height: 43px;
    gap: 6px;
    padding: 6px;
  }

  .card.compact .mana {
    width: 13px;
  }

  .card.compact .spell-copy strong {
    font-size: 0.82rem;
  }

  .card.compact .spell-copy span,
  .card.compact .flavor,
  .card.compact .traits,
  .card.compact .link {
    display: none;
  }

  .card.compact .power {
    min-width: 26px;
    font-size: 0.96rem;
  }

  .card.compact .status-pill,
  .card.compact .serial {
    min-height: 23px;
    padding-inline: 7px;
    font-size: 0.56rem;
  }

  .card.showcase {
    min-height: 540px;
  }

  .card.showcase .art-frame {
    aspect-ratio: 0.78;
  }

  .card.showcase h3 {
    font-size: 1.46rem;
  }

  @keyframes foil-drift {
    0%, 100% {
      transform: translateX(-26%);
      opacity: 0.34;
    }

    45% {
      transform: translateX(18%);
      opacity: 0.68;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .card[data-premium='true']::before {
      animation: none;
    }

    img {
      transition: none;
    }
  }
`;

export class CardFaceElement extends HTMLElement {
  private readonly root: ShadowRoot;
  private currentCard: CardData | null = null;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set card(value: CardData | null) {
    this.currentCard = value;
    this.render();
  }

  get card(): CardData | null {
    return this.currentCard;
  }

  connectedCallback(): void {
    this.render();
  }

  private render(): void {
    const card = this.currentCard;
    if (!card) {
      this.root.innerHTML = '';
      return;
    }

    const compact = this.hasAttribute('compact');
    const showcase = this.hasAttribute('showcase');
    const meta = card.metadata as CardVisualMeta;
    const premium = ['rare', 'epic', 'legendary'].includes(card.rarity);
    const finish = textMeta(meta, 'finish', premium ? 'holo' : 'standard');
    const count = Number(this.getAttribute('data-count') ?? '1');
    const status = this.getAttribute('data-status') ?? (count > 1 ? `x${count}` : 'filed');
    const typeLabel = textMeta(meta, 'typeLabel', card.tags[0] ?? 'mate');
    const hp = textMeta(meta, 'hp', defaultHp(card.rarity));
    const spellName = textMeta(meta, 'spellName', defaultSpellName(card));
    const spellPower = textMeta(meta, 'spellPower', defaultSpellPower(card.rarity));
    const spellCost = numberMeta(meta, 'spellCost', defaultSpellCost(card.rarity));
    const flavor = textMeta(meta, 'flavor', card.subtitle ?? '');
    const weakness = textMeta(meta, 'weakness', defaultWeakness(card.rarity));
    const resistance = textMeta(meta, 'resistance', defaultResistance(card.rarity));
    const retreat = textMeta(meta, 'retreat', defaultRetreat(card.rarity));
    const cardCode = textMeta(meta, 'cardCode', shortSerial(card));
    const fallback = initialsFor(card);

    this.root.innerHTML = `
      <style>${styles}</style>
      <article class="card ${compact ? 'compact' : ''} ${showcase ? 'showcase' : ''}" data-rarity="${card.rarity}" data-premium="${premium}" data-finish="${escapeHtml(finish)}">
        <div class="inner">
          <div class="topline">
            <span class="type-pill">${escapeHtml(typeLabel)}</span>
            <div class="nameplate">
              <h3>${escapeHtml(card.name)}</h3>
              <span class="subtitle">${escapeHtml(card.subtitle ?? 'Mate card')}</span>
            </div>
            <span class="hp"><strong>${escapeHtml(hp)}</strong><small>HP</small><i aria-hidden="true"></i></span>
          </div>

          <div class="art-frame" data-fallback="${escapeHtml(fallback)}">
            <img src="${getCardArt(card)}" alt="${escapeHtml(card.name)}" loading="${compact || showcase ? 'eager' : 'lazy'}" />
          </div>

          <div class="spell-row">
            <div class="cost" aria-label="${spellCost} spell cost">${renderMana(spellCost)}</div>
            <div class="spell-copy">
              <strong>${escapeHtml(spellName)}</strong>
              <span>${escapeHtml(card.description)}</span>
            </div>
            <div class="power">${escapeHtml(spellPower)}</div>
          </div>

          ${flavor ? `<p class="flavor">${escapeHtml(flavor)}</p>` : ''}

          <div class="traits">
            ${renderTrait('Weakness', weakness)}
            ${renderTrait('Resist', resistance)}
            ${renderTrait('Retreat', retreat)}
          </div>

          <div class="footer">
            <span class="status-pill">${escapeHtml(status)}</span>
            ${card.sourceUrl ? `<a class="link" href="${escapeHtml(card.sourceUrl)}" target="_blank" rel="noreferrer">Source</a>` : ''}
            <span class="serial">#${escapeHtml(cardCode)}</span>
          </div>
        </div>
      </article>
    `;

    const image = this.root.querySelector('img');
    image?.addEventListener('load', () => image.setAttribute('data-loaded', 'true'), { once: true });
    image?.addEventListener(
      'error',
      () => {
        image.src = buildFallbackArt(card);
      },
      { once: true }
    );
    if (image?.complete && image.naturalWidth > 0) {
      image.setAttribute('data-loaded', 'true');
    }
  }
}

if (!customElements.get('gacha-card')) {
  customElements.define('gacha-card', CardFaceElement);
}

function renderMana(cost: number): string {
  return Array.from({ length: Math.max(1, Math.min(5, cost)) }, () => '<span class="mana"></span>').join('');
}

function renderTrait(label: string, value: string): string {
  return `
    <span class="trait">
      <small>${escapeHtml(label)}</small>
      <strong>${escapeHtml(value)}</strong>
    </span>
  `;
}

function getCardArt(card: CardData): string {
  return card.imageUrl || buildFallbackArt(card);
}

function buildFallbackArt(card: CardData): string {
  const initials = initialsFor(card);
  const accent = rarityAccent(card.rarity);
  const subtitle = card.subtitle ?? 'Mate card';
  const tagLine = card.tags.slice(0, 2).join(' / ') || 'Collector card';
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 1200">
      <defs>
        <linearGradient id="bg" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stop-color="${accent}" />
          <stop offset="100%" stop-color="#08100c" />
        </linearGradient>
        <linearGradient id="shine" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.35" />
          <stop offset="42%" stop-color="#ffffff" stop-opacity="0" />
          <stop offset="70%" stop-color="#ffffff" stop-opacity="0.18" />
          <stop offset="100%" stop-color="#ffffff" stop-opacity="0" />
        </linearGradient>
      </defs>
      <rect width="900" height="1200" rx="54" fill="url(#bg)" />
      <rect width="900" height="1200" rx="54" fill="url(#shine)" />
      <path d="M84 160h732M84 970h732" stroke="#ffffff" stroke-opacity="0.18" stroke-width="6" />
      <rect x="70" y="70" width="760" height="1060" rx="36" fill="#000000" fill-opacity="0.18" stroke="#ffffff" stroke-opacity="0.2" />
      <text x="96" y="166" fill="#ffffff" fill-opacity="0.74" font-family="Segoe UI, sans-serif" font-size="34" font-weight="800">${escapeXml(card.rarity.toUpperCase())}</text>
      <text x="96" y="430" fill="#ffffff" font-family="Segoe UI, sans-serif" font-size="212" font-weight="900">${escapeXml(initials)}</text>
      <text x="96" y="522" fill="#ffffff" fill-opacity="0.9" font-family="Segoe UI, sans-serif" font-size="48" font-weight="800">${escapeXml(trimToLength(card.name, 26))}</text>
      <text x="96" y="580" fill="#ffffff" fill-opacity="0.72" font-family="Segoe UI, sans-serif" font-size="32">${escapeXml(trimToLength(subtitle, 40))}</text>
      <text x="96" y="1030" fill="#ffffff" fill-opacity="0.86" font-family="Segoe UI, sans-serif" font-size="28">${escapeXml(trimToLength(tagLine, 42))}</text>
    </svg>
  `.trim();

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function initialsFor(card: CardData): string {
  return (
    card.name
      .split(/\s+/)
      .slice(0, 2)
      .map((chunk) => chunk[0]?.toUpperCase() ?? '')
      .join('') || 'MG'
  );
}

function shortSerial(card: CardData): string {
  const prefix = initialsFor(card);
  return `${prefix}-${String((stableHash(card.id) % 900) + 100)}`;
}

function stableHash(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
}

function textMeta(meta: CardVisualMeta, key: keyof CardVisualMeta, fallback: string): string {
  const value = meta[key];
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function numberMeta(meta: CardVisualMeta, key: keyof CardVisualMeta, fallback: number): number {
  const value = meta[key];
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

function defaultSpellName(card: CardData): string {
  return card.stats[0]?.label || 'Signature Move';
}

function defaultHp(rarity: RarityTier): string {
  switch (rarity) {
    case 'legendary':
      return '230';
    case 'epic':
      return '190';
    case 'rare':
      return '160';
    case 'uncommon':
      return '130';
    case 'common':
      return '110';
  }
}

function defaultSpellPower(rarity: RarityTier): string {
  switch (rarity) {
    case 'legendary':
      return '140';
    case 'epic':
      return '100';
    case 'rare':
      return '70';
    case 'uncommon':
      return '50';
    case 'common':
      return '30';
  }
}

function defaultSpellCost(rarity: RarityTier): number {
  switch (rarity) {
    case 'legendary':
      return 5;
    case 'epic':
      return 4;
    case 'rare':
      return 3;
    case 'uncommon':
      return 2;
    case 'common':
      return 2;
  }
}

function defaultWeakness(rarity: RarityTier): string {
  return rarity === 'legendary' ? 'Hype x2' : 'Delay x2';
}

function defaultResistance(rarity: RarityTier): string {
  return rarity === 'common' ? 'None' : 'Noise -20';
}

function defaultRetreat(rarity: RarityTier): string {
  return rarity === 'legendary' || rarity === 'epic' ? '3 tabs' : '1 tab';
}

function rarityAccent(rarity: RarityTier): string {
  switch (rarity) {
    case 'legendary':
      return '#f5c76b';
    case 'epic':
      return '#e16f9d';
    case 'rare':
      return '#68c7ff';
    case 'uncommon':
      return '#58d68d';
    case 'common':
      return '#9eaa9b';
  }
}

function trimToLength(value: string, maxLength: number): string {
  return value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;
}

function escapeHtml(value: string): string {
  return escapeXml(value);
}

function escapeXml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}
